import { Subject } from "rxjs";

import Environment from "../environment";
import { AuthService } from "./AuthService.js";
import i18n from "i18n";

const code500Subject = new Subject();

const Api = {
    code500Subject: code500Subject,

    //
    //GENERIC
    getHeaders: function(method = "GET") {
        const headers = {};
        headers["Accept-Language"] = i18n.language;
        if (AuthService.currentUserValue()) {
            let token = AuthService.getToken();
            headers["Authorization"] = token;
            return headers;
        }
    },

    handleErrors: function(response) {
        if (response.ok) {
            return response;
        }
        if (response.status === 401) {
            AuthService.logout();
        }
        if (response.status === 403) {
            response.json().then(data => {
                if (
                    "code" in data &&
                    (data["code"] === "token_not_valid" ||
                        data["code"] === "user_not_found" ||
                        data["code"] === "user_inactive")
                ) {
                    AuthService.logout();
                }
            });
        }
        if (response.status >= 500) {
            code500Subject.next(response);
        }
        throw response;
    },

    getUrlWithParams: function(path, params = {}) {
        let url = new URL(path);
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined) {
                url.searchParams.append(key, params[key]);
            }
        });
        return url;
    },

    fetch: (url, method = "GET", data = null) => {
        const headers = Api.getHeaders();
        const info = Object.assign(
            {},
            {
                method,
                headers: {
                    ...headers,
                    "Content-Type": "application/json"
                }
            },
            data && method !== "GET" ? { body: JSON.stringify(data) } : null
        );

        url = data && method === "GET" ? Api.getUrlWithParams(url, data) : url;

        return fetch(url, info)
            .then(Api.handleErrors)
            .then(resp => {
                if (resp.status === 204) {
                    return null;
                }
                return resp.json();
            });
    },

    fetchNoToken: (url, method = "GET", data = null) => {
        const info = Object.assign(
            {},
            {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Accept-Language": i18n.language
                }
            },
            data && method !== "GET" ? { body: JSON.stringify(data) } : null
        );

        url = data && method === "GET" ? Api.getUrlWithParams(url, data) : url;

        return fetch(url, info)
            .then(Api.handleErrors)
            .then(resp => {
                if (resp.status === 204) {
                    return null;
                }
                return resp.json();
            });
    },

    fetchDownload: (url, method = "GET", data = null) => {
        const headers = Api.getHeaders();
        const info = Object.assign(
            {},
            {
                method,
                headers: {
                    ...headers,
                    "Content-Type": "application/json"
                }
            },
            data && method !== "GET" ? { body: JSON.stringify(data) } : null
        );

        url = data && method === "GET" ? Api.getUrlWithParams(url, data) : url;

        return fetch(url, info)
            .then(Api.handleErrors)
            .then(resp =>
                resp.blob().then(blob => ({
                    blob,
                    filename: Api.filenameFromContentDisposition(
                        resp.headers.get("Content-Disposition")
                    )
                }))
            );
    },

    filenameFromContentDisposition: header => {
        if (!header) {
            return null;
        }

        const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
        const plain = /filename="?([^";]+)"?/i.exec(header);
        let name = null;

        if (encoded) {
            name = encoded[1].trim().replace(/^"|"$/g, "");
            try {
                name = decodeURIComponent(name);
            } catch (error) {
                // Keep the raw header value when it is not percent-encoded.
            }
        } else if (plain) {
            name = plain[1].trim();
        }

        if (!name) {
            return null;
        }

        name = name.replace(/[/\\]/g, "").replace(/[\u0000-\u001f]/g, "");

        return name || null;
    },

    filenameFromDownloadUrl: url => {
        try {
            const parts = new URL(url).pathname.split("/").filter(Boolean);
            const imprimirAt = parts.lastIndexOf("imprimir");
            const candidate = imprimirAt > 0 ? decodeURIComponent(parts[imprimirAt - 1]) : "";

            if (
                candidate &&
                candidate !== "comprobantes" &&
                candidate !== "comprobantes-cobrados"
            ) {
                return candidate.toLowerCase().endsWith(".pdf")
                    ? candidate
                    : `${candidate}.pdf`;
            }
        } catch (error) {
            // Fall through to the generic name.
        }

        return "comprobante.pdf";
    },

    comprobantePdfUrl: (unidad, comprobante, periodo) => {
        const url = `${Environment.api}api/unidades-facturaciones/${unidad}/comprobantes/${encodeURIComponent(comprobante)}/imprimir`;
        const periodoNormalizado = periodo == null ? "" : String(periodo).trim();

        if (periodoNormalizado === "" || periodoNormalizado === "-") {
            return url;
        }

        return `${url}?periodo=${encodeURIComponent(periodoNormalizado)}`;
    },

    openAuthenticatedDownload: url => {
        return Api.fetchDownload(url).then(({ blob, filename }) => {
            const name = filename || Api.filenameFromDownloadUrl(url);
            const file =
                typeof File === "function"
                    ? new File([blob], name, {
                          type: blob.type || "application/pdf"
                      })
                    : blob;
            const objectUrl = window.URL.createObjectURL(file);
            const tab = window.open(objectUrl, "_blank");

            if (!tab) {
                const link = document.createElement("a");
                link.href = objectUrl;
                link.download = name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
        });
    },

    fetchUpload: (url, method = "POST", form) => {
        const headers = Api.getHeaders();
        const info = Object.assign(
            {},
            {
                method,
                headers: {
                    ...headers
                    // 'Content-Type': 'multipart/form-data'
                },
                body: form
            }
        );

        return fetch(url, info)
            .then(Api.handleErrors)
            .then(resp => {
                if (resp.status === 204) {
                    return null;
                }
                return resp.json();
            });
    }
};

export default Api;
